// Service-team member form: validation and request bodies. The rules mirror
// the backend (schemas/catalog.py) so the common mistakes are caught before a
// round trip; the server still answers 409 `phone_taken`, 422
// `address_not_found`, `coverage_overlap`, ... for what only it can know.
import { isValidTime, isValidDate } from './helpers';

const NAME_MAX = 120;
const EMAIL_MAX = 255;
const PHONE_MIN = 6;
const PHONE_MAX = 32;
const ADDRESS_MIN = 3;
const REASON_MAX = 255;

export function emptyCoverageRow() {
  return { weekday: 0, site_id: '', start: '', end: '' };
}

export function emptyLeave() {
  return { date_from: '', date_to: '', reason: '' };
}

export function emptyMember() {
  return {
    name: '',
    phone: '',
    email: '',
    role_key: '',
    address: '',
    hours_start: '09:00',
    hours_end: '18:00',
    active: true,
    coverage: [],
    leave: [],
  };
}

export function memberToForm(member) {
  return {
    name: member.name,
    phone: member.phone,
    email: member.email || '',
    role_key: member.role_key,
    address: member.address || '',
    hours_start: member.hours_start || '',
    hours_end: member.hours_end || '',
    active: !!member.active,
    coverage: (member.coverage || []).map(row => ({
      weekday: row.weekday,
      site_id: row.site_id || '',
      start: row.start,
      end: row.end,
    })),
    leave: (member.leave || []).map(l => ({
      date_from: l.date_from,
      date_to: l.date_to,
      reason: l.reason || '',
    })),
  };
}

// A coverage row without times uses the member's hours.
export function effectiveCoverage(form) {
  return form.coverage.map(row => ({
    ...row,
    start: row.start || form.hours_start,
    end: row.end || form.hours_end,
  }));
}

// Returns { field: CODE } with the codes of CO_LIVING.TEAM.ERRORS. The
// coverage and leave problems carry the row index as `coverage:<i>` and
// `leave:<i>` so the modal can mark the row.
export function validateMember(form) {
  const errors = {};
  const name = form.name.trim();
  if (!name || name.length > NAME_MAX) errors.name = 'NAME';
  const phone = form.phone.trim();
  if (phone.length < PHONE_MIN || phone.length > PHONE_MAX) {
    errors.phone = 'PHONE';
  }
  if (form.email.trim().length > EMAIL_MAX) errors.email = 'EMAIL';
  if (!form.role_key) errors.role_key = 'ROLE';
  if (form.address.trim().length < ADDRESS_MIN) errors.address = 'ADDRESS';

  const hasStart = !!form.hours_start;
  const hasEnd = !!form.hours_end;
  if (hasStart !== hasEnd) {
    errors.hours = 'HOURS_BOTH';
  } else if (hasStart) {
    if (!isValidTime(form.hours_start) || !isValidTime(form.hours_end)) {
      errors.hours = 'HOURS_BOTH';
    } else if (form.hours_start >= form.hours_end) {
      errors.hours = 'HOURS_ORDER';
    }
  }

  const rows = effectiveCoverage(form);
  rows.forEach((row, index) => {
    if (errors.coverage !== undefined) return;
    if (!isValidTime(row.start) || !isValidTime(row.end)) {
      errors.coverage = `NO_TIMES:${index}`;
    } else if (row.start >= row.end) {
      errors.coverage = `ORDER:${index}`;
    }
  });
  if (errors.coverage === undefined) {
    for (let a = 0; a < rows.length; a += 1) {
      for (let b = a + 1; b < rows.length; b += 1) {
        if (
          rows[a].weekday === rows[b].weekday &&
          rows[a].start < rows[b].end &&
          rows[b].start < rows[a].end
        ) {
          errors.coverage = `OVERLAP:${b}`;
        }
      }
    }
  }

  form.leave.forEach((leave, index) => {
    if (errors.leave !== undefined) return;
    if (
      !isValidDate(leave.date_from) ||
      !isValidDate(leave.date_to) ||
      leave.date_to < leave.date_from
    ) {
      errors.leave = `DATES:${index}`;
    } else if (leave.reason.length > REASON_MAX) {
      errors.leave = `REASON:${index}`;
    }
  });

  return errors;
}

// Splits "ORDER:2" into { code: 'ORDER', index: 2 }.
export function splitRowError(value) {
  if (!value) return null;
  const [code, index] = value.split(':');
  return { code, index: Number(index) };
}

export function memberPayload(form) {
  return {
    name: form.name.trim(),
    phone: form.phone.trim(),
    email: form.email.trim() || null,
    role_key: form.role_key,
    address: form.address.trim(),
    hours_start: form.hours_start || null,
    hours_end: form.hours_end || null,
    active: form.active,
    coverage: effectiveCoverage(form).map(row => ({
      weekday: Number(row.weekday),
      site_id: row.site_id || null,
      start: row.start,
      end: row.end,
    })),
    leave: form.leave.map(l => ({
      date_from: l.date_from,
      date_to: l.date_to,
      reason: l.reason.trim(),
    })),
  };
}

// Coverage rows grouped by weekday (Monday first), for the list.
export function coverageByDay(coverage) {
  const days = {};
  (coverage || []).forEach(row => {
    days[row.weekday] = days[row.weekday] || [];
    days[row.weekday].push(row);
  });
  return Object.keys(days)
    .map(Number)
    .sort((a, b) => a - b)
    .map(weekday => ({ weekday, rows: days[weekday] }));
}
